import { useState, useEffect, useRef } from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../config/firebase";

// Bump cache keys so we don't reuse old API-based cache
const CACHE_KEY = "courses_cache_v2";
const CACHE_EXPIRY_KEY = "courses_cache_expiry_v2";
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

const useCourses = (options = {}) => {
  const { lazy = false } = options;
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(!lazy); // if lazy, start not loading
  const [error, setError] = useState(null);
  const fetchedRef = useRef(false); // prevent duplicate fetches

  // Check if cache is valid
  const isCacheValid = () => {
    const cachedExpiry = localStorage.getItem(CACHE_EXPIRY_KEY);
    if (!cachedExpiry) return false;
    return new Date().getTime() < parseInt(cachedExpiry, 10);
  };

  // Load courses from cache
  const loadFromCache = () => {
    try {
      const cachedCourses = localStorage.getItem(CACHE_KEY);
      if (cachedCourses) {
        const parsedCourses = JSON.parse(cachedCourses);
        console.log("Loaded courses from cache:", parsedCourses.length);
        return parsedCourses;
      }
    } catch (error) {
      console.error("Error loading from cache:", error);
    }
    return null;
  };

  // Save courses to cache
  const saveToCache = (coursesData) => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(coursesData));
      localStorage.setItem(
        CACHE_EXPIRY_KEY,
        (new Date().getTime() + CACHE_DURATION).toString(),
      );
      console.log("Saved courses to cache:", coursesData.length);
    } catch (error) {
      console.error("Error saving to cache:", error);
    }
  };

  const fetchCourses = async () => {
    // Avoid duplicate fetches
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    // Check cache first (new v2 keys only)
    if (isCacheValid()) {
      const cachedCourses = loadFromCache();
      if (cachedCourses) {
        setCourses(cachedCourses);
        setError(null);
        setLoading(false);
        return;
      }
    }

    try {
      setLoading(true);
      console.log("Fetching courses from Firestore CcpCourses collection...");

      const coursesRef = collection(db, "CcpCourses");
      const q = query(coursesRef, orderBy("Courses"));
      const snapshot = await getDocs(q);

      // Normalize to the shape expected by SearchableCourseSelect
      const coursesData = snapshot.docs.map((doc) => {
        const data = doc.data();
        const courseCode = data.Courses || "";
        return {
          id: doc.id,
          ...data,
          // Backward-compatible fields for components expecting Course_Nm
          Course_Nm: courseCode,
          Course_Cd: courseCode,
          Course_Title_Ds: "", // no separate title in Firestore
        };
      });

      if (coursesData.length > 0) {
        if (process.env.NODE_ENV !== "production") {
          console.log(
            "First Firestore course structure (normalized):",
            coursesData[0],
          );
          console.log("Total courses found in Firestore:", coursesData.length);
        }

        // Save to cache for next time
        saveToCache(coursesData);
      }

      setCourses(coursesData);
      setError(null);
    } catch (err) {
      console.error("Error fetching courses from Firestore:", err);
      setError(err.message || "Failed to load courses");
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!lazy) {
      fetchCourses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lazy]);

  return { courses, loading, error, fetchCourses };
};

export default useCourses;
